import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Switch,
    Avatar,
    Paper,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slider,
} from "@mui/material";
import {
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Info as InfoIcon,
    Logout as LogoutIcon,
    PhotoCamera as PhotoCameraIcon,
    Description as MyDocumentsIcon,
    Gavel as GavelIcon,
    PrivacyTip as PrivacyTipIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import ProfileService from "../services/Profile.service";
import {
    getSplashSpeed,
    saveSplashSpeed,
    MIN_SPLASH_SPEED,
    MAX_SPLASH_SPEED,
} from "../utils/splashSpeed";

// CSS px size of the draggable circular preview in the photo editor dialog —
// must match the Box width/height below so on-screen dragging lines up with
// what cropAndUploadPhoto() actually draws onto the (higher-res) output canvas.
const PHOTO_PREVIEW_SIZE = 260;

// ── Settings Page ─────────────────────────────────────────────────────────── //
export default function Settings() {
    const { mode, toggleTheme } = useThemeMode();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [alert, setAlert] = useState(null);
    const [groups, setGroups] = useState([]);
    const [userPhoto, setUserPhoto] = useState(() => localStorage.getItem("userPhoto") || null);
    const [splashSpeed, setSplashSpeed] = useState(getSplashSpeed);
    const [displayName, setDisplayName] = useState("");
    const [initials, setInitials] = useState("?");
    const [hasDocuments, setHasDocuments] = useState(false);

    // Photo editor state
    const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
    const [tempImage, setTempImage] = useState(null);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [imageScale, setImageScale] = useState(1);
    const [initialScale, setInitialScale] = useState(1);
    const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);
    const imageRef = useRef(null);

    useEffect(() => {
        ProfileService.getMe()
            .then(({ data }) => applyProfileData(data))
            .catch(() => setAlert({ type: "error", text: "Failed to load profile." }))
            .finally(() => setLoading(false));
    }, []);

    const applyProfileData = (data) => {
        const first = data.first_name || "";
        const last = data.last_name || "";
        const name = [first, last].filter(Boolean).join(" ") || data.username || "";
        setDisplayName(name);
        setInitials(
            `${first[0] || ""}${last[0] || ""}`.toUpperCase() ||
            data.username?.[0]?.toUpperCase() ||
            "?"
        );
        setGroups(data.groups || []);
        setHasDocuments((data.employee_documents || []).length > 0);
        const imgUrl = data.profile?.profile_image || null;
        setUserPhoto(imgUrl);
        if (imgUrl) localStorage.setItem("userPhoto", imgUrl);
        else localStorage.removeItem("userPhoto");
        window.dispatchEvent(new Event("userPhotoUpdated"));
    };

    // Photo upload
    const handlePhotoUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setAlert({ type: "error", text: "File size must be less than 5 MB." });
            return;
        }
        if (!file.type.startsWith("image/")) {
            setAlert({ type: "error", text: "Please upload an image file." });
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            const img = new Image();
            img.onload = () => {
                imageRef.current = img;
                setTempImage(reader.result);
                const size = PHOTO_PREVIEW_SIZE;
                const scale = Math.max(size / img.width, size / img.height);
                const position = {
                    x: (size - img.width * scale) / 2,
                    y: (size - img.height * scale) / 2,
                };
                setImageScale(scale);
                setInitialScale(scale);
                setImagePosition(position);
                setInitialPosition(position);
                setPhotoEditorOpen(true);
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
        event.target.value = "";
    };

    // Mouse drag
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    };
    const handleMouseMove = (e) => {
        if (isDragging && imageRef.current)
            setImagePosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    };
    const handleMouseUp = () => setIsDragging(false);

    // Touch drag (mobile)
    const handleTouchStart = (e) => {
        const t = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: t.clientX - imagePosition.x, y: t.clientY - imagePosition.y });
    };
    const handleTouchMove = (e) => {
        if (!isDragging || !imageRef.current) return;
        e.preventDefault();
        const t = e.touches[0];
        setImagePosition({ x: t.clientX - dragStart.x, y: t.clientY - dragStart.y });
    };

    const handleWheel = (e) => {
        e.preventDefault();
        setImageScale((s) => Math.min(Math.max(0.5, s + e.deltaY * -0.001), 3));
    };

    const cropAndUploadPhoto = () => {
        const canvas = canvasRef.current;
        if (!canvas || !imageRef.current) return;
        const ctx = canvas.getContext("2d");
        const outputSize = 300;
        canvas.width = outputSize;
        canvas.height = outputSize;
        ctx.save();
        ctx.beginPath();
        ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        const img = imageRef.current;
        const ratio = outputSize / PHOTO_PREVIEW_SIZE;
        ctx.drawImage(
            img,
            imagePosition.x * ratio,
            imagePosition.y * ratio,
            img.width * imageScale * ratio,
            img.height * imageScale * ratio,
        );
        ctx.restore();
        setPhotoEditorOpen(false);
        setTempImage(null);
        setUploadingPhoto(true);
        setAlert(null);
        canvas.toBlob(
            async (blob) => {
                try {
                    const { data } = await ProfileService.uploadProfileImage(blob);
                    applyProfileData(data);
                    setAlert({ type: "success", text: "Profile photo updated." });
                } catch {
                    setAlert({ type: "error", text: "Failed to upload photo." });
                } finally {
                    setUploadingPhoto(false);
                }
            },
            "image/jpeg",
            0.9,
        );
    };

    const handleRemovePhoto = async () => {
        setUploadingPhoto(true);
        setAlert(null);
        try {
            const { data } = await ProfileService.deleteProfileImage();
            applyProfileData(data);
            setAlert({ type: "success", text: "Profile photo removed." });
        } catch {
            setAlert({ type: "error", text: "Failed to remove photo." });
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={6}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {alert && (
                <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                    {alert.text}
                </Alert>
            )}

            {/* ── Profile Photo ── */}
            <Typography variant="overline" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                Profile Photo
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {uploadingPhoto ? (
                        <Box sx={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Avatar
                            src={userPhoto || undefined}
                            sx={{ width: 72, height: 72, bgcolor: "primary.main", fontSize: "1.5rem", fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                        >
                            {!userPhoto && initials}
                        </Avatar>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, mb: 0.5 }}>
                            {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {user?.email || user?.username || ""}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<PhotoCameraIcon />}
                                size="small"
                                disabled={uploadingPhoto}
                            >
                                {userPhoto ? "Change" : "Upload"}
                                <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                            </Button>
                            {userPhoto && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={handleRemovePhoto}
                                    disabled={uploadingPhoto}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* ── Access Groups ── */}
            {groups.length > 0 && (
                <>
                    <Typography variant="overline" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                        Access Groups
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {groups.map((g) => (
                                <Chip key={g} label={g} color="primary" variant="outlined" size="small" />
                            ))}
                        </Box>
                    </Paper>
                </>
            )}

            {/* ── My Documents ── */}
            {hasDocuments && (
                <>
                    <Typography variant="overline" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                        My Documents
                    </Typography>
                    <Paper variant="outlined" sx={{ mb: 3 }}>
                        <List disablePadding>
                            <ListItem disablePadding>
                                <ListItemButton onClick={() => navigate("/my-documents")}>
                                    <ListItemIcon><MyDocumentsIcon /></ListItemIcon>
                                    <ListItemText
                                        primary="My Documents"
                                        secondary="Personal documents on file for you, not project files"
                                    />
                                    <ChevronRightIcon fontSize="small" color="action" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Paper>
                </>
            )}

            {/* ── Appearance ── */}
            <Typography variant="overline" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                Appearance
            </Typography>
            <Paper variant="outlined" sx={{ mb: 3 }}>
                <List disablePadding>
                    <ListItem>
                        <ListItemIcon>
                            {mode === "dark" ? <DarkModeIcon /> : <LightModeIcon />}
                        </ListItemIcon>
                        <ListItemText primary="Dark Mode" secondary={mode === "dark" ? "On" : "Off"} />
                        <ListItemSecondaryAction>
                            <Switch
                                edge="end"
                                checked={mode === "dark"}
                                onChange={toggleTheme}
                                inputProps={{ "aria-label": "Toggle dark mode" }}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem sx={{ flexDirection: "column", alignItems: "stretch", py: 2 }}>
                        <ListItemText
                            primary="Splash Screen Speed"
                            secondary={splashSpeed === MIN_SPLASH_SPEED ? "Normal" : `${splashSpeed.toFixed(2)}x faster`}
                            sx={{ mb: 1 }}
                        />
                        <Box sx={{ px: 1 }}>
                            <Slider
                                value={splashSpeed}
                                onChange={(_, v) => setSplashSpeed(v)}
                                onChangeCommitted={(_, v) => saveSplashSpeed(v)}
                                min={MIN_SPLASH_SPEED}
                                max={MAX_SPLASH_SPEED}
                                step={0.25}
                                marks={[
                                    { value: MIN_SPLASH_SPEED, label: "Normal" },
                                    { value: MAX_SPLASH_SPEED, label: "Fast" },
                                ]}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${v.toFixed(2)}x`}
                                aria-label="Splash screen speed"
                            />
                        </Box>
                    </ListItem>
                </List>
            </Paper>

            {/* ── About ── */}
            <Typography variant="overline" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                About
            </Typography>
            <Paper variant="outlined" sx={{ mb: 3 }}>
                <List disablePadding>
                    <ListItem>
                        <ListItemIcon><InfoIcon /></ListItemIcon>
                        <ListItemText
                            primary="Kodex Shielding Solutions Portal"
                            secondary={`v${process.env.REACT_APP_VERSION || "dev"}`}
                        />
                    </ListItem>
                </List>
            </Paper>

            {/* ── Legal ── */}
            <Typography variant="overline" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                Legal
            </Typography>
            <Paper variant="outlined" sx={{ mb: 3 }}>
                <List disablePadding>
                    <ListItem disablePadding divider>
                        <ListItemButton onClick={() => navigate("/terms")}>
                            <ListItemIcon><GavelIcon /></ListItemIcon>
                            <ListItemText primary="Terms of Service" />
                            <ChevronRightIcon fontSize="small" color="action" />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton onClick={() => navigate("/privacy")}>
                            <ListItemIcon><PrivacyTipIcon /></ListItemIcon>
                            <ListItemText primary="Privacy Policy" />
                            <ChevronRightIcon fontSize="small" color="action" />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Paper>

            {/* ── Sign Out ── */}
            <Button
                variant="outlined"
                color="error"
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ mb: 3 }}
            >
                Sign Out
            </Button>

            {/* ── Photo Editor Dialog ── */}
            <Dialog
                open={photoEditorOpen}
                onClose={() => { setPhotoEditorOpen(false); setTempImage(null); }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Adjust Profile Photo</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 1 }}>
                        <Box
                            sx={{
                                position: "relative",
                                width: PHOTO_PREVIEW_SIZE,
                                height: PHOTO_PREVIEW_SIZE,
                                borderRadius: "50%",
                                overflow: "hidden",
                                border: "2px solid",
                                borderColor: "divider",
                                cursor: isDragging ? "grabbing" : "grab",
                                userSelect: "none",
                                touchAction: "none",
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onWheel={handleWheel}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleMouseUp}
                        >
                            {tempImage && imageRef.current && (
                                <img
                                    src={tempImage}
                                    alt="Preview"
                                    style={{
                                        position: "absolute",
                                        left: imagePosition.x,
                                        top: imagePosition.y,
                                        width: imageRef.current.width * imageScale,
                                        height: imageRef.current.height * imageScale,
                                        pointerEvents: "none",
                                    }}
                                    draggable={false}
                                />
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            Drag to reposition · Pinch or use slider to zoom
                        </Typography>
                        <Box sx={{ width: "100%", px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Zoom</Typography>
                                <Button size="small" sx={{ textTransform: "none", p: 0, minWidth: 0 }} onClick={() => { setImageScale(initialScale); setImagePosition(initialPosition); }}>
                                    Reset
                                </Button>
                            </Box>
                            <Slider value={imageScale} onChange={(_, v) => setImageScale(v)} min={0.5} max={3} step={0.05} />
                        </Box>
                        <canvas ref={canvasRef} style={{ display: "none" }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPhotoEditorOpen(false); setTempImage(null); }}>Cancel</Button>
                    <Button onClick={cropAndUploadPhoto} variant="contained" disableElevation>
                        Save Photo
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
